import * as zod from 'zod';

import { ActionRequiredError, Step } from '@sb/remote-control/types';
import { TeleopModeEnum, type TeleopMode } from '@sb/routine-runner';

export { TeleopModeEnum, type TeleopMode };

// Display labels for teleop modes (update when adding modes to TeleopModeEnum)
export const TELEOP_MODE_LABELS: Record<TeleopMode, string> = {
  arHeadset: 'AR Headset',
  iosPhone: 'iPhone',
  iosTablet: 'iPad',
  hapticDevice: 'Haptic Device',
};

export namespace TeleopStep {
  export const name = 'Teleoperation';
  export const description =
    'Pause routine and wait for teleoperator to take control via AR headset';
  export const librarySection = Step.LibrarySection.InterfaceWithOperators;
  export const librarySort = '3';
  export const argumentKind = 'Teleop';

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    mode: TeleopModeEnum.default('iosTablet'),
    timeoutSeconds: zod.number().int().positive().default(300),
    tags: zod.array(zod.string().max(30)).default([]),
    gripperVelocity: zod.number().positive().optional(),
    gripperMin: zod.number().min(0).max(1).optional(),
    gripperMax: zod.number().min(0).max(1).optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  // Teleoperation only functions on a robot in echo (AI) mode. Mark the step
  // invalid everywhere else (fail closed while the identity is unknown) so
  // the editor shows a warning and play is blocked, like a move step with no
  // waypoints — instead of the step timing out obscurely at runtime.
  export const validator: Step.Validator = ({
    stepConfiguration,
    robotOperationMode,
  }) => {
    if (stepConfiguration?.args?.argumentKind !== argumentKind) {
      return;
    }

    if (robotOperationMode !== 'echo') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Teleoperation requires the robot to be in Echo (AI) mode.',
      });
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Teleop',
      args: {
        mode: args.mode,
        timeoutSeconds: args.timeoutSeconds,
        tags: args.tags,
        gripperVelocity: args.gripperVelocity,
        gripperMin: args.gripperMin,
        gripperMax: args.gripperMax,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    return [
      includeStepName ? 'Teleop' : false,
      `wait up to ${args.timeoutSeconds}s for operator`,
    ]
      .filter((line) => line !== false)
      .join(': ');
  };
}

TeleopStep satisfies Step.StepKindInfo;
